interface Delegation {
    readonly source: string;
    readonly perform: () => Promise<string>;
    readonly resolve: (value: string) => void;
    readonly reject: (error: unknown) => void;
    readonly promise: Promise<string>;
}

/** One session's bounded work; completed tokens are never cached across authorization checks. */
export class ImageValidationDelegations {
    private readonly pending = new Map<string, Delegation>();
    private readonly queue: Delegation[] = [];
    private active = 0;
    private stopped = false;

    public run(source: string, perform: () => Promise<string>): Promise<string> {
        if (this.stopped) return Promise.resolve(source);
        const existing = this.pending.get(source);
        if (existing !== undefined) return existing.promise;
        if (this.pending.size >= 68) return Promise.reject(new Error("Validation delegation capacity exceeded"));
        let resolve: (value: string) => void = () => undefined;
        let reject: (error: unknown) => void = () => undefined;
        const promise = new Promise<string>((res, rej) => {
            resolve = res;
            reject = rej;
        });
        const entry = { source, perform, resolve, reject, promise };
        this.pending.set(source, entry);
        this.queue.push(entry);
        this.drain();
        return promise;
    }

    public stop(): void {
        this.stopped = true;
        for (const entry of this.pending.values()) entry.resolve(entry.source);
        this.pending.clear();
        this.queue.length = 0;
    }

    private drain(): void {
        while (!this.stopped && this.active < 4 && this.queue.length > 0) {
            const entry = this.queue.shift();
            if (entry === undefined) return;
            this.active++;
            void this.perform(entry);
        }
    }

    private async perform(entry: Delegation): Promise<void> {
        try {
            const result = await entry.perform();
            entry.resolve(this.stopped ? entry.source : result);
        } catch (error: unknown) {
            // Prevent another queued request starting before the caller handles the failure.
            entry.reject(error);
            this.stop();
        } finally {
            this.pending.delete(entry.source);
            this.active--;
            this.drain();
        }
    }
}
